import React, { useState, useRef } from 'react';
import { Upload, FileText, X, Link, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import { isValidFileType, getFileInputAccept, getFileTypeInfo } from '../utils/fileUpload';

interface ArticleSuppliersDataUploadProps {
    databaseId?: number | string | null;
    onUploadSuccess?: () => void;
}

interface UploadResult {
    message: string;
    created: number;
    updated: number;
    total_rows: number;
    errors_count: number;
    suppliers_auto_created: number;
    auto_created_suppliers?: string[];
    errors?: string[];
    additional_errors?: number;
}

interface UploadResultModalProps {
    isOpen: boolean;
    onClose: () => void;
    result: UploadResult;
}

const UploadResultModal: React.FC<UploadResultModalProps> = ({ isOpen, onClose, result }) => {
    if (!isOpen) return null;

    const hasErrors = result.errors_count > 0;
    const hasWarnings = result.errors && result.errors.length > 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-2xl mx-4 max-h-[80vh] overflow-y-auto w-full">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${hasErrors ? 'bg-amber-100' : 'bg-purple-100'}`}>
                            {hasErrors ? (
                                <AlertTriangle className={`${hasErrors ? 'text-amber-600' : 'text-purple-600'}`} size={20} />
                            ) : (
                                <CheckCircle className="text-purple-600" size={20} />
                            )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Résultat de l'import des relations
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div className="space-y-4">
                    {/* Summary Statistics */}
                    <div className={`p-4 rounded-lg border ${hasErrors ? 'bg-amber-50 border-amber-200' : 'bg-purple-50 border-purple-200'}`}>
                        <h4 className={`font-medium mb-3 ${hasErrors ? 'text-amber-900' : 'text-purple-900'}`}>
                            {hasErrors ? 'Import terminé avec des erreurs' : 'Import terminé avec succès'}
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className={hasErrors ? 'text-amber-800' : 'text-purple-800'}>
                                <span className="font-medium">Relations créées:</span> {result.created}
                            </div>
                            <div className={hasErrors ? 'text-amber-800' : 'text-purple-800'}>
                                <span className="font-medium">Relations mises à jour:</span> {result.updated}
                            </div>
                            <div className={hasErrors ? 'text-amber-800' : 'text-purple-800'}>
                                <span className="font-medium">Total lignes:</span> {result.total_rows}
                            </div>
                            <div className={hasErrors ? 'text-amber-800' : 'text-purple-800'}>
                                <span className="font-medium">Erreurs:</span> {result.errors_count}
                            </div>
                        </div>
                        
                        {result.created + result.updated > 0 && (
                            <div className="mt-3 p-2 bg-white bg-opacity-50 rounded border text-xs">
                                <strong>Succès:</strong> {result.created + result.updated} relation(s) traitée(s) avec succès
                            </div>
                        )}
                    </div>

                    {/* Auto-created Suppliers */}
                    {result.suppliers_auto_created > 0 && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                                <CheckCircle size={16} />
                                Fournisseurs créés automatiquement
                            </h4>
                            <p className="text-blue-800 text-sm mb-2">
                                <strong>{result.suppliers_auto_created} fournisseur(s)</strong> ont été créés automatiquement 
                                avec des valeurs par défaut :
                            </p>
                            {result.auto_created_suppliers && (
                                <div className="space-y-1">
                                    {result.auto_created_suppliers.map((supplier, index) => (
                                        <div key={index} className="text-xs text-blue-700 p-1 bg-white bg-opacity-50 rounded">
                                            • {supplier}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-blue-700 text-xs mt-2">
                                💡 Vous pouvez mettre à jour leurs informations via l'import des fournisseurs.
                            </p>
                        </div>
                    )}

                    {/* Error Details */}
                    {hasWarnings && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <h4 className="font-medium text-red-900 mb-3 flex items-center gap-2">
                                <AlertTriangle size={16} />
                                Détails des erreurs
                            </h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {result.errors!.map((error, index) => (
                                    <div key={index} className="text-sm text-red-800 p-2 bg-white bg-opacity-50 rounded border">
                                        {error}
                                    </div>
                                ))}
                            </div>
                            
                            {result.additional_errors && result.additional_errors > 0 && (
                                <div className="mt-3 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                                    <strong>Note:</strong> {result.additional_errors} erreur(s) supplémentaire(s) non affichée(s). 
                                    Consultez les logs du serveur pour la liste complète.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Success Message */}
                    {!hasErrors && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-800 text-sm">
                                🎉 Toutes les relations articles-fournisseurs ont été importées avec succès ! 
                                Vous pouvez maintenant utiliser ces informations pour la planification 
                                d'approvisionnement et le calcul des volumes.
                            </p>
                        </div>
                    )}
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                            hasErrors 
                                ? 'bg-amber-600 text-white hover:bg-amber-700' 
                                : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
};

interface FormatModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FormatModal: React.FC<FormatModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-3xl mx-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Format Fichier Articles-Fournisseurs</h3>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Colonnes requises</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                            <li><strong>article_ref</strong> (obligatoire): Référence de l'article (doit exister dans la base d'articles).</li>
                            <li><strong>supplier_name</strong> (obligatoire): Nom du fournisseur (créé automatiquement s'il n'existe pas).</li>
                            <li><strong>minimum_order_quantity</strong> (obligatoire): MOQ (entier positif).</li>
                            <li><strong>unit_volume_m3</strong> (obligatoire): Volume unitaire en m³ (nombre positif).</li>
                            <li><strong>quantity_per_pallet</strong> (obligatoire): Quantité par palette (entier positif).</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Règles d'import</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                            <li><strong>Formats:</strong> {getFileTypeInfo().formats}</li>
                            <li><strong>Encodages CSV:</strong> utf-8, latin1, cp1252 (détection automatique)</li>
                            <li><strong>Délimiteurs CSV:</strong> ; , tabulation, | (détection automatique)</li>
                            <li><strong>Excel multi-feuilles:</strong> seule la première feuille est lue</li>
                            <li><strong>Taille conseillée:</strong> ≤ 50 Mo</li>
                            <li>Un couple (article_ref, supplier_name) existant sera <strong>mis à jour</strong> (MOQ, volume, quantité/palette).</li>
                            <li>Si le fournisseur n'existe pas, il est <strong>créé automatiquement</strong> avec <code className="font-mono">default_delivery_delay=1</code> et <code className="font-mono">location_group=Unknown</code>.</li>
                            <li>Les valeurs non numériques ou négatives sont rejetées.</li>
                        </ul>
                    </div>
                    
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Exemple:</h4>
                        <div className="p-3 bg-gray-50 rounded border text-xs font-mono overflow-x-auto">
                            article_ref,supplier_name,minimum_order_quantity,unit_volume_m3,quantity_per_pallet<br/>
                            A,Fournisseur A,1400,0.0005,2623<br/>
                            B,Fournisseur B,1500,0.0011,1309<br/>
                            C,Fournisseur A,1500,0.0009,1676
                        </div>
                    </div>
                    
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-sm text-blue-800">
                            <strong>Note:</strong> Colonnes attendues exactement <code className="font-mono">article_ref,supplier_name,minimum_order_quantity,unit_volume_m3,quantity_per_pallet</code>.
                        </p>
                    </div>
                    
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                        <p className="text-sm text-amber-800">
                            <strong>Important:</strong> Les articles référencés doivent déjà exister dans votre base de données.
                        </p>
                    </div>
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                        Compris
                    </button>
                </div>
            </div>
        </div>
    );
};

const ArticleSuppliersDataUpload: React.FC<ArticleSuppliersDataUploadProps> = ({ databaseId, onUploadSuccess }) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showFormatModal, setShowFormatModal] = useState<boolean>(false);
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
    const [showResultModal, setShowResultModal] = useState<boolean>(false);
    const { activeDatabase } = useDatabaseContext();
    const inputRef = useRef<HTMLInputElement>(null);

    // Find the target database ID - prioritize databaseId prop over activeDatabase
    const targetDatabaseId = databaseId || activeDatabase?.id || null;

    // Use shared file type validation

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const selectedFile = event.target.files[0];
            if (isValidFileType(selectedFile)) {
                setFile(selectedFile);
                setError('');
                setUploadResult(null);
            } else {
                setError(getFileTypeInfo().errorMessage);
            }
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            if (isValidFileType(droppedFile)) {
                setFile(droppedFile);
                setError('');
                setUploadResult(null);
            } else {
                setError(getFileTypeInfo().errorMessage);
            }
        }
    };

    const handleClick = () => {
        inputRef.current?.click();
    };

    const removeFile = () => {
        setFile(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        setError('');
        setUploadResult(null);
    };

    const handleSubmitWithConfirmation = () => {
        if (!file || !targetDatabaseId) {
            setError('Veuillez sélectionner un fichier');
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleCloseResultModal = () => {
        setShowResultModal(false);
        setUploadResult(null);
    };

    const handleConfirmedSubmit = async () => {
        if (!file || !targetDatabaseId) return;

        const formData = new FormData();
        formData.append('file', file);

        setLoading(true);
        setError('');
        setShowConfirmDialog(false);
        setUploadResult(null);

        try {
            const response = await api.post(API_ROUTES.ARTICLE_SUPPLIERS.BASE + API_ROUTES.ARTICLE_SUPPLIERS.UPLOAD(String(targetDatabaseId)), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setUploadResult(response.data);
            setShowResultModal(true);
            setFile(null);
            
            // Call success callback if provided
            onUploadSuccess?.();
            
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            
        } catch (err: any) {
            setError(err.response?.data?.error || 'Une erreur est survenue lors de l\'import');
        } finally {
            setLoading(false);
        }
    };

    if (!targetDatabaseId) {
        return (
            <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-yellow-800 text-sm">
                    Aucune base de données sélectionnée. Veuillez sélectionner une base de données avant d'importer les relations articles-fournisseurs.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-100 rounded-xl">
                        <Link className="text-purple-600" size={24} />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Relations Articles-Fournisseurs
                        </h2>
                        <p className="text-gray-600 text-sm">
                            Importez les relations entre articles et fournisseurs pour {activeDatabase?.name || 'votre base'}
                        </p>
                    </div>
                </div>
                
                <button
                    onClick={() => setShowFormatModal(true)}
                    className="flex items-center gap-2 px-3 py-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                >
                    <Info size={16} />
                    Format Fichier
                </button>
            </div>

            {/* Drag & Drop Area - Compact */}
            <div
                className={`relative border-2 border-dashed rounded-xl p-4 transition-all cursor-pointer group ${
                    dragActive 
                        ? 'border-purple-400 bg-purple-50' 
                        : file 
                            ? 'border-green-400 bg-green-50' 
                            : 'border-gray-300 hover:border-purple-300 hover:bg-purple-50/30'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={inputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept={getFileInputAccept()}
                    className="hidden"
                    disabled={loading}
                />
                
                {file ? (
                    // File Selected State - Horizontal Layout
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                                <FileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900 text-sm truncate">{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeFile();
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <p className="text-gray-600 text-xs">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </div>
                        </div>
                        
                        {/* Import Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSubmitWithConfirmation();
                            }}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm flex-shrink-0"
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Upload size={16} />
                            )}
                            {loading ? 'Import...' : 'Importer'}
                        </button>
                    </div>
                ) : (
                    // Drop Zone State - Horizontal Layout
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg transition-all flex-shrink-0 ${
                            dragActive ? 'bg-purple-200' : 'bg-gray-100 group-hover:bg-purple-100'
                        }`}>
                            <Link className={`transition-colors ${
                                dragActive ? 'text-purple-600' : 'text-gray-400 group-hover:text-purple-500'
                            }`} size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 mb-1">
                                {dragActive ? 'Déposez votre fichier ici' : 'Glissez vos relations articles-fournisseurs'}
                            </h3>
                            <p className="text-xs text-gray-600">
                                ou <span className="text-purple-600 font-medium">cliquez pour parcourir</span>
                                {' • '}
                                <span className="text-gray-500">CSV, XLSX, XLS</span>
                                {' • '}
                                <span className="text-gray-500">{getFileTypeInfo().formatsShort}</span>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-md mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <AlertTriangle className="text-purple-600" size={20} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Confirmer l'import
                            </h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Êtes-vous sûr de vouloir importer ces relations articles-fournisseurs ? 
                            Les relations existantes seront mises à jour et de nouveaux fournisseurs peuvent être créés automatiquement.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirmedSubmit}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                Confirmer l'import
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Format Modal */}
            <FormatModal isOpen={showFormatModal} onClose={() => setShowFormatModal(false)} />

            {/* Upload Result Modal */}
            {uploadResult && (
                <UploadResultModal 
                    isOpen={showResultModal}
                    onClose={handleCloseResultModal}
                    result={uploadResult}
                />
            )}

            {/* Error Messages */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-3">
                    <X className="text-red-500 mt-0.5 flex-shrink-0" size={16} />
                    {error}
                </div>
            )}
        </div>
    );
};

export default ArticleSuppliersDataUpload; 